import React, { useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { GoAFormItem } from '@abgov/react-components';
import { InputAdornment, TextField, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

interface Props {
  label: string;
  placeholder: string;
  search: (value: string) => void;
  clearSearch: () => void;
  maxLength?: number; // Optional maxLength parameter
}

export interface SearchInputRef {
  setSearchValue: (value: string) => void;
}

const SearchInput = forwardRef<SearchInputRef, Props>(
  ({ label, placeholder, search, clearSearch, maxLength }, ref) => {
    const [searchValue, setSearchValue] = useState('');

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearchValue(value);
      search(value);
    };

    const handleClearSearch = () => {
      setSearchValue('');
      clearSearch();
    };

    // Expose setSearchValue to the parent component
    useImperativeHandle(ref, () => ({
      setSearchValue,
    }));

    return (
      <GoAFormItem label={label}>
        <TextField
          variant="outlined"
          fullWidth
          value={searchValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          inputProps={{
            maxLength: maxLength || undefined, // Enforces maxLength at the input level
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end">
                  {searchValue ? <ClearIcon /> : <SearchIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </GoAFormItem>
    );
  }
);

SearchInput.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  search: PropTypes.func.isRequired,
  clearSearch: PropTypes.func.isRequired,
  maxLength: PropTypes.number, // Define maxLength as an optional prop type
};

export default SearchInput;
